import { bacnetObjectTypeShortNameToColor, bacnetTypeIdToShortName } from '@constants/bacnet';
import useTypes from '@hooks/useTypes';
import { useStoreActions } from '@models/RootStore';
import { smartConvertToStringFormats } from '@utils/StringUtils';
import { useState, useRef, useMemo } from 'react';


const useBacNet = () => {
  const {
    getSiteBacnetParameterTemplates,
    getBacnetUnitObjects,
    getBacnetUnitById,
    createBacnetParameterTemplate,
    updateBacnetParameterTemplate,
    deleteBacnetParameterTemplate,
    getSiteBacnetUnits,
    fetchSiteBacnetDevices: storeFetchSiteBacnetDevices,
    assignBacnetParameterTemplate,
    unassignBacnetParameterTemplate,
    updateBacnetUnit,
    assignBacnetUiTemplate,
    unassignBacnetUiTemplate,
    deleteBacnetUnit,
    updateBacnetDevice,
    discoverBacnetUnitsByDevice: handleDiscoverBacnetUnitsByDevice,
    discoverBacnetUnitsBySite: handleDiscoverBacnetUnitsBySite,
    discoverBacnetDeviceObjects: handleDiscoverBacnetDeviceObjects,
    importBacnetTemplates,
    getBacnetPriorityArray,
    resetBacnetPriority,
    getObjectValues: storeGetObjectValues,
    setObjectValues: storeSetObjectValues,
  } = useStoreActions((action) => action.bacnetDevices);

  const {bacnetObjectTypes} = useTypes();

  /**
 * Memoized mapping of BACnet object type IDs to capitalized names.
 * @type {Object<string, string>}
 */
  const capitalizedNamesByObjectTypeId = useMemo(() => {
    if (!bacnetObjectTypes){
      return {}; // Return empty object if bacnetObjectTypes is undefined
    }
    return Object.entries(bacnetObjectTypes).reduce((newNamesObject, [key, value]) => {
      newNamesObject[key] = smartConvertToStringFormats(value).capitalized;
      return newNamesObject;
    }, {});
  }, [bacnetObjectTypes]);



  const { addMessage } = useStoreActions((action) => action.errorMessage);

  // State for tracking BacNet parameter templates
  const [siteBacNetParameterTemplates, setSiteBacNetParameterTemplates] =
    useState({});
  const [siteBacNetUnits, setSiteBacNetUnits] = useState({});
  const [siteBacNetDevices, setSiteBacNetDevices] = useState({});

  // Ref to track the latest siteBacNetUnits state synchronously
  const siteBacNetUnitsRef = useRef({});
  siteBacNetUnitsRef.current = siteBacNetUnits;

  // State for tracking multiple loading statuses
  const isLoadingRef = useRef({});

  // Using useRef to track loading states
  const withLoader = async (key, asyncFn, silent = false) => {
    if (!silent && isLoadingRef.current[key]) {
      return;
    }
    if (!silent) {
      isLoadingRef.current[key] = true;
    }
    try {
      const result = await asyncFn();
      return result;
    } catch (error) {
      addMessage({ message: error.message || 'An error occurred' });
      throw error;
    } finally {
      if (!silent) {
        isLoadingRef.current[key] = false;
      }
    }
  };

  // Fetch Site BacNet Units and store the result with a specific loader key
  const getSiteBacNetUnits = async (siteId, silent = false) => {
    return withLoader('getSiteBacNetUnits', async () => {
      const res = await getSiteBacnetUnits(siteId);
      setSiteBacNetUnits(res);
      return res;
    }, silent);
  };

  // Fetch Site BacNet Devices and store the result with a specific loader key
  const getSiteBacNetDevices = async (siteId, silent = false, onlyNew = false) => {
    return withLoader('getSiteBacNetDevices', async () => {
      const res = await storeFetchSiteBacnetDevices({siteId,onlyNew});
      setSiteBacNetDevices(res);
      return res;
    }, silent);
  };

  // Used for background polling: bypasses withLoader so a failure isn't reported through the
  // global error dialog on its own. The caller decides whether to surface it (e.g. only while
  // whatever is watching this poll is still open), since withLoader would show it unconditionally
  // even for a request that resolves after the caller stopped caring.
  const pollSiteBacNetDevices = async (siteId, onlyNew = false) => {
    return storeFetchSiteBacnetDevices({ siteId, onlyNew });
  };

  // Fetch BacNet Unit by ID with a specific loader key
  const getBacNetUnitById = async (unitId) => {
    return withLoader('getBacNetUnitById', async () => await getBacnetUnitById(unitId));
  };

  // Used for background polling: transient network failures (e.g. an idle/backgrounded tab)
  // must not surface the blocking error dialog. Returns null on failure so the caller can retry.
  const pollBacNetUnitById = async (unitId) => {
    try {
      return await getBacnetUnitById(unitId);
    } catch (error) {
      return null;
    }
  };

  // Delete BacNet Unit by ID with a specific loader key
  const deleteBacNetUnit = async (unitId) => {
    return withLoader('deleteBacNetUnit', async () => await deleteBacnetUnit(unitId));
  };

  // Fetch BacNet Unit objects with a specific loader key
  const getBacNetUnitObjects = async (bacnetUnitId) => {
    return withLoader('getBacNetUnitObjects', () =>
      getBacnetUnitObjects(bacnetUnitId)
    );
  };

  // Fetch Site BacNet Parameter Templates and store the result with a specific loader key
  const getSiteBacNetParameterTemplates = async (siteId, silent = false) => {
    return withLoader('getSiteBacNetParameterTemplates', async () => {
      const templates = await getSiteBacnetParameterTemplates(siteId);
      setSiteBacNetParameterTemplates(templates);
      return templates;
    }, silent);
  };

  // Create BacNet Parameter Template with a specific loader key
  const createBacNetParameterTemplate = async (template) => {
    return withLoader('createBacNetParameterTemplate', async () => {
      const createdTemplate = await createBacnetParameterTemplate(template);
      setSiteBacNetParameterTemplates({
        ...siteBacNetParameterTemplates,
        [createdTemplate.id]: createdTemplate,
      });
      return createdTemplate;
    });
  };

  // Create BacNet Parameter Template with a specific loader key
  const createMultipleBacNetParameterTemplates = async (templateDetailsArray = []) => {
    return withLoader('createMultipleBacNetParameterTemplates', () => {

      if(!templateDetailsArray.length){
        return [];
      }

      const promises = templateDetailsArray.map(({newTemplateDetails,entityTemplate})=>{
        const params = { ...newTemplateDetails, entityTemplate }
        if(entityTemplate){
          params.entityType = 'controlUnit';
        }
        return createBacnetParameterTemplate(params);
      });

      return Promise.allSettled(promises).then((allResult)=>{

        const list = [];
        const object = {};

        allResult.forEach(({status,value},index) => {
          if(status === 'fulfilled'){
            list.push({...templateDetailsArray[index],fullTemplate:value});
            object[value.id] = value;
          }
        });
        setSiteBacNetParameterTemplates({
          ...siteBacNetParameterTemplates,
          ...object
        });
        return list;
      });
    });
  };

  // Update BacNet Parameter Template with a specific loader key
  const updateBacNetParameterTemplate = async (data) => {
    return withLoader('updateBacNetParameterTemplate', async () => {
      const updatedTemplate = await updateBacnetParameterTemplate(data);
      setSiteBacNetParameterTemplates((prevState) => ({
        ...prevState,
        [updatedTemplate.id]: updatedTemplate,
      }));
      return updatedTemplate;
    });
  };

    // Delete BacNet Parameter Template with a specific loader key
  const deleteBacNetParameterTemplate = async (data) => {
    return withLoader('deleteBacnetParameterTemplate', async () => {
      await deleteBacnetParameterTemplate(data);
    });
  };



  const updateMultipleBacNetParameterTemplates = async (dataArray) => {
    return withLoader('updateBacNetParameterTemplate', async () => {

      const promises = dataArray.map((data)=>{
        return updateBacnetParameterTemplate(data);
      });

      return Promise.allSettled(promises).then((allResult)=>{

        const list = [];
        const object = {};

        allResult.forEach(({status,value}) => {
          if(status === 'fulfilled'){
            list.push(value);
            object[value.id] = value;
          }
        });

        setSiteBacNetParameterTemplates((prevState) => ({
          ...prevState,
          ...object,
        }));

        return list;
      });
    });
  };

  // Assign BacNet Parameter Template with a specific loader key
  const assignBacNetParameterTemplate = async (templateId, bacnetUnitId) => {
    return withLoader('assignBacNetParameterTemplate', async () => {
      await assignBacnetParameterTemplate({ templateId, bacnetUnitId });
      const isNewTemplate = siteBacNetUnits?.[bacnetUnitId]?.compatibleParamTemplates.filter(t => t.id === templateId).length === 0;
      if (isNewTemplate) {
        const res = await getSiteBacnetUnits(siteBacNetUnits?.[bacnetUnitId]?.site);
        setSiteBacNetUnits(res);
        siteBacNetUnitsRef.current = res;
      }else{
        const updatedUnits = {
          ...siteBacNetUnitsRef.current,
          [bacnetUnitId]: {
            ...siteBacNetUnitsRef.current[bacnetUnitId],
            paramTemplate: templateId,
            uiTemplate: null,
          },
        };
        setSiteBacNetUnits(updatedUnits);
        siteBacNetUnitsRef.current = updatedUnits;
      }
    });
  };

  // Unassign BacNet Parameter Template with a specific loader key
  const unassignBacNetParameterTemplate = async (templateId, bacnetUnitId) => {
    return withLoader('unassignBacNetParameterTemplate', async () => {

      // Build the server data object with templateId and bacnetUnitId
      const serverData = { templateId, bacnetUnitId };

      // If templateId is not provided, attempt to retrieve it from cache or server
      if(!templateId){
        // First, try to get the templateId from the cached siteBacNetUnits ref
        const cachedTemplateId = siteBacNetUnitsRef.current?.[bacnetUnitId]?.paramTemplate;

        if(cachedTemplateId){
          // Use the cached templateId if available
          serverData.templateId = cachedTemplateId;
        }else{
          // If not in cache, fetch the bacnet unit from the server
          const bacnetUnit = await getBacNetUnitById(bacnetUnitId);

          if(bacnetUnit.paramTemplate){
        // Set the templateId from the fetched unit
        serverData.templateId = bacnetUnit.paramTemplate;
          }else{
        // Log error if no template is found to unassign
        console.error('No template to unassign found for bacnet unit', bacnetUnitId);
          }
        }
      }
      if(serverData.templateId){
        await unassignBacnetParameterTemplate(serverData);
      }
      setSiteBacNetUnits((prevState) => ({
        ...prevState,
        [bacnetUnitId]: {
          ...prevState[bacnetUnitId],
          paramTemplate: null,
          uiTemplate: null,
        },
      }));
    });
  };

  // Assign BacNet UI Template with a specific loader key
  const assignBacNetUiTemplate = async (uiTemplateId, paramTemplateId , bacnetUnitId) => {
    return withLoader('assignBacNetUiTemplate', async () => {

      // Use the ref to get the most up-to-date state, avoiding unnecessary fetch calls
      const currentBacnetUnit = siteBacNetUnitsRef.current?.[bacnetUnitId] ?? {};

      // Make sure we have the paramTemplate loaded
      const currentBacnetUnitParamTemplate = currentBacnetUnit?.paramTemplate;

      // If we don't have the unit in state at all, or unit exists with no paramTemplate then fetch it
      // But if we have it, trust that it's current.
      const bacnetUnit = currentBacnetUnitParamTemplate ? currentBacnetUnit : await getBacNetUnitById(bacnetUnitId);

      if(bacnetUnit.paramTemplate !== paramTemplateId) {
        await assignBacnetParameterTemplate({ templateId: paramTemplateId, bacnetUnitId });
        // Update our ref immediately to reflect the change
        siteBacNetUnitsRef.current = {
          ...siteBacNetUnitsRef.current,
          [bacnetUnitId]: {
            ...siteBacNetUnitsRef.current[bacnetUnitId],
            paramTemplate: paramTemplateId,
            uiTemplate: null,
          },
        };
      }
      if(bacnetUnit.uiTemplate !== uiTemplateId) {
        await assignBacnetUiTemplate({ templateId: uiTemplateId, bacnetUnitId });
      }

      const updatedUnits = {
        ...siteBacNetUnitsRef.current,
        [bacnetUnitId]: {
          ...siteBacNetUnitsRef.current[bacnetUnitId],
          uiTemplate: uiTemplateId,
          paramTemplate: paramTemplateId,
        },
      };
      setSiteBacNetUnits(updatedUnits);
      siteBacNetUnitsRef.current = updatedUnits;
    });
  };

  // Unassign BacNet UI Template with a specific loader key
  const unassignBacNetUiTemplate = async (templateId, bacnetUnitId) => {
    return withLoader('unassignBacNetUiTemplate', async () => {

      const params = { templateId, bacnetUnitId };

      if (!templateId) {
        // Use the ref to get the most up-to-date state, avoiding unnecessary fetch calls
        const currentBacnetUnit =
          siteBacNetUnitsRef.current?.[bacnetUnitId] ?? {};

        // Make sure we have the uiTemplate loaded
        const currentBacnetUnitUiTemplate = currentBacnetUnit?.uiTemplate;

        // If we don't have the unit in state at all, or unit exists with no uiTemplate then fetch it
        // But if we have it, trust that it's current.
        const bacnetUnit = currentBacnetUnitUiTemplate
          ? currentBacnetUnit
          : await getBacNetUnitById(bacnetUnitId);

        // update params with the templateId from either cache or server
        params.templateId = bacnetUnit.uiTemplate;
      }
      await unassignBacnetUiTemplate(params);
      setSiteBacNetUnits((prevState) => ({
        ...prevState,
        [bacnetUnitId]: {
          ...prevState[bacnetUnitId],
          uiTemplate: null,
        },
      }));
    });
  };

  // Update BacNet Unit with a specific loader key
  const updateBacNetUnit = async (data) => {
    return withLoader('updateBacNetUnit', async () => {
      const updatedUnit = await updateBacnetUnit(data);
      setSiteBacNetUnits((prevState) => ({
        ...prevState,
        [data.id]: {
          ...prevState[data.id],
          ...data.data,
        },
      }));
      return updatedUnit;
    });
  };

  // Update BacNet Device with a specific loader key
  const updateBacNetDevice = async (data) => {
    return withLoader('updateBacNetDevice', async () => {
      const updatedDevice = await updateBacnetDevice(data);
      setSiteBacNetDevices((prevState) => ({
        ...prevState,
        [data.id]: {
          ...prevState[data.id],
          ...data.data,
        },
      }));
      return updatedDevice;
    });
  };

  // Update multiple BacNet Devices with a specific loader key
  const updateMultipleBacNetDevices = async (devices) => {
    return withLoader('updateMultipleBacNetDevices', async () => {
      const results = await Promise.all(
        devices.map((device) => updateBacnetDevice(device))
      );
      setSiteBacNetDevices((prevState) => {
        const nextState = { ...prevState };
        devices.forEach((device) => {
          nextState[device.id] = {
            ...nextState[device.id],
            ...device.data,
          };
        });
        return nextState;
      });
      return results;
    });
  };

  const discoverBacnetUnitsByDevice = async (deviceId, line) => {
    return withLoader('discoverBacnetUnitsByDevice', async () => await handleDiscoverBacnetUnitsByDevice({
      deviceId,
      line
    }));
  };

  const discoverBacnetUnitsBySite = async (siteId) => {
    return withLoader('discoverBacnetUnitsBySite', async () => await handleDiscoverBacnetUnitsBySite({
      siteId
    }));
  };

  const discoverBacnetDeviceObjects = async (bacnetDeviceId) => {
    return withLoader('discoverBacnetDeviceObjects', async () => await handleDiscoverBacnetDeviceObjects({
      bacnetDeviceId
    }));
  };

  // Fetch BacNet Priority Array with a specific loader key
  const getBacNetPriorityArray = async (bacnetId, objectId) => {
    return withLoader('getBacNetPriorityArray', async () => await getBacnetPriorityArray({
      bacnetId,
      objectId
    }));
  };

  // Reset BacNet Priority with a specific loader key
  const resetBacNetPriority = async (bacnetId, objectId, priority) => {
    return withLoader('resetBacNetPriority', async () => await resetBacnetPriority({
      bacnetId,
      objectId,
      priority
    }));
  };

  const readObjectValues = async (bacnetDeviceId, objectIds) => {
    return withLoader('getObjectValues', () => storeGetObjectValues({ bacnetDeviceId, objects: objectIds }), true);
  };

  const writeObjectValues = async (bacnetDeviceId, objects) => {
    return withLoader('setObjectValues', () => storeSetObjectValues({ bacnetDeviceId, objects }));
  };

  /**
 * Returns the color, name, and short name associated with a given BACnet type ID.
 * - If the type ID is found in `bacnetTypeIdToShortName`, it returns the corresponding short name and mapped color.
 * - If the short name is not in `bacnetObjectTypeShortNameToColor`, defaults to "magenta".
 * - If the type ID is not in `bacnetTypeIdToShortName`, defaults the short name to the type ID string.
 * - The `name` is retrieved from `capitalizedNamesByObjectTypeId` based on the type ID, or defaults to the short name.
 *
 * @param {number} typeId - The BACnet type ID to look up.
 * @returns {{ color: string, shortName: string, name: string }} - An object containing the color, short name, and name for the type ID.
 */
  const getBacnetIconInformation = (typeId) => {
    const shortName = bacnetTypeIdToShortName[typeId] || typeId.toString(); // Fallback to typeId as string if not found
    const name = capitalizedNamesByObjectTypeId[typeId] || shortName;
    const color = bacnetObjectTypeShortNameToColor[shortName] || 'magenta'; // Default to magenta if color not found

    return { color, shortName, name };
  };

  return {
    isLoading: isLoadingRef.current,
    siteBacNetParameterTemplates,
    getBacNetUnitById,
    pollBacNetUnitById,
    getBacNetUnitObjects,
    getSiteBacNetParameterTemplates,
    createBacNetParameterTemplate,
    updateBacNetParameterTemplate,
    deleteBacNetParameterTemplate,
    getSiteBacNetUnits,
    siteBacNetUnits,
    siteBacNetDevices,
    getSiteBacNetDevices,
    pollSiteBacNetDevices,
    assignBacNetParameterTemplate,
    unassignBacNetParameterTemplate,
    updateBacNetUnit,
    updateBacNetDevice,
    updateMultipleBacNetDevices,
    assignBacNetUiTemplate,
    unassignBacNetUiTemplate,
    getBacnetIconInformation,
    deleteBacNetUnit,
    createMultipleBacNetParameterTemplates,
    updateMultipleBacNetParameterTemplates,
    discoverBacnetUnitsByDevice,
    discoverBacnetUnitsBySite,
    discoverBacnetDeviceObjects,
    importBacnetTemplates,
    getBacNetPriorityArray,
    resetBacNetPriority,
    readObjectValues,
    writeObjectValues,
  };
};

export default useBacNet;
